<script setup>
import ActionButton from "@/Components/Buttons/Action.vue"
import { nextTick, ref, watch } from "vue"
import axios from "axios"
import { debounce } from "lodash";
import { useForm } from "@inertiajs/vue3";
import { ComboboxInput, Combobox, ComboboxOptions, ComboboxOption } from "@headlessui/vue"

const props = defineProps({
  callback: {
    required: true,
  },
  close: {
    required: true,
  },
})

const query = ref('')
const results = ref({})
const loading = ref (false)

const getSearchResults = (query) => {
  if (query !== '') {
    loading.value = true
    axios.get(route('hq.auctions.algolia.search'), {
      params: {
        search: query,
      }
    }).then((res) => {
      results.value = res.data
    }).finally(() => {
      loading.value = false
    })
  } else {
    results.value = {}
    loading.value = false
  }
}

const debouncedSearch = debounce(getSearchResults, 250)
watch(query, debouncedSearch)

const attachAuctionForm = useForm({
  auction: null,
})

watch(() => attachAuctionForm.auction, (auction) => addAuction(auction))

const addAuction = (auction) => {
  props.callback(auction)
}
</script>

<template>
  <vue-final-modal
    classes="flex justify-center items-start modal-overflow bg-black bg-opacity-25"
    content-class="relative flex flex-col w-full h-full items-center justify-center border dark:border-gray-800 rounded-lg shadow-lg modal-overflow"
  >
    <div class="absolute w-full md:max-w-[640px] p-4 bg-white shadow-md rounded space-y-4 z-50">
      <div class="mt-4">
        <label for="searchAuctions">
          {{ __('Search auctions') }}
        </label>
        <div class="">
          <div class="relative">
            <Combobox v-model="attachAuctionForm.auction">
              <div class="relative flex mt-1">
                <ComboboxInput
                  class="w-full rounded-md border bg-white py-2 pl-3 pr-10 shadow-sm focus:border-primary-300 focus:ring-primary-200/50 sm:text-sm"
                  @change="query = $event.target.value"
                  autocomplete="none"
                />
                <svg v-if="loading" class="animate-spin mr-3 h-5 w-5 text-gray-500 absolute top-2.5 right-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <ComboboxOptions
                v-if="Object.keys(results).length > 0"
                class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
              >
                <ComboboxOption
                  v-for="option in results"
                  :key="option.id"
                  v-slot="{ active, selected }"
                  :value="option"
                  as="template"
                >
                  <li
                    :class="[
                      'relative cursor-default select-none py-2 pl-3 pr-9',
                      active ? 'bg-primary-600 text-white' : 'text-gray-900',
                    ]"
                  >
                    <span class="opacity-50 text-xs">ID: {{ option.id }}</span>
                    <span :class="['block truncate', selected && 'font-semibold']">
                      {{ option.title }}
                    </span>
                  </li>
                </ComboboxOption>
              </ComboboxOptions>
            </Combobox>
          </div>
        </div>
        <div class="text-xs mt-2 text-gray-500">
          <p>{{ __('Enter auction id to find') }}</p>
        </div>
      </div>
      <ActionButton as="button" @click.prevent="close">{{ __('Close') }}</ActionButton>
    </div>
  </vue-final-modal>
</template>
